<template>
  <div class="service-selector" :class="classList">
    <searchable-service-list
      v-if="showSearchList"
      class="search-list"
      :query="query"
      @queryChange="handleQueryChange"
      :isLoading="isLoading"
      :value="selectableServices"
      :disabled="disabled"
      :showAddAll="showAddAll"
      @addAll="handleClickAddAllButton"
      :hasMoreServices="hasMoreServices"
      @select="handleSelectService"
      @getMoreServices="setupServices"
      ref="searchableServiceList"
    ></searchable-service-list>
    <selected-service-list
      :disabled="disabled"
      class="selected-list"
      :value="value"
      @remove="handleRemoveService"
      @removeAll="handleRemoveAllService"
    ></selected-service-list>
  </div>
</template>

<script>
import differenceWith from "ramda/src/differenceWith";
import { Input } from "element-ui";
import ServiceSelectorOption from "./ServiceSelectorOption";
import SelectedServiceList from "./SelectedServiceList";
import SearchableServiceList from "./SearchableServiceList";
import ScButton from "@/lib/components/ScButton";
import { baanRueJaiClient } from "@/dependencies";
import { debounce } from "@/lib/util";

export default {
  components: {
    ServiceSelectorOption,
    "el-input": Input,
    SelectedServiceList,
    SearchableServiceList,
    ScButton
  },
  props: ["initialServices", "value", "disabled", "showAddAll"],
  data() {
    return {
      query: "",
      allServices: [],
      isLoading: false,
      page: 1,
      hasMoreServices: false,
      totalServices: 1
    };
  },
  computed: {
    selectableServices() {
      return differenceWith(this.compareService, this.allServices, this.value);
    },
    disabledRemoveButton() {
      return this.value.length === 0;
    },
    showSearchList() {
      return !this.disabled;
    },
    classList() {
      return { disabled: this.disabled };
    },
    queryParams() {
      return {
        name: this.query,
        page: this.page
      }
    }
  },
  watch: {
    query(oldVal, newVal) {
      this.watchQuery();
    }
  },
  created() {
    this.setup();
  },
  methods: {
    setup() {
      this.setupData();
    },
    setupData() {
      this.setupServices();
    },
    async setupServices() {
      if (this.allServices.length === this.totalServices) {
        this.hasMoreServices = false;
        return;
      }
      const params = this.queryParams;
      const services = await this.fetchServices(params);
      this.page += 1;
      this.hasMoreServices = true;

      const newServices = services.map(service => {
        const {
          id,
          name,
          short_description: shortDescription,
          description,
          icon_url: iconUrl,
          options,
          quota
        } = service;
        const provider = {
          ...service.provider,
          logoUrl: service.provider.logo_url
        };

        return {
          id,
          iconUrl,
          name,
          shortDescription,
          description,
          provider,
          options,
          quota
        };
      });

      this.allServices = this.allServices.concat(newServices);
    },
    async fetchServices(params = {}) {
      if (this.initialServices) {
        return this.initialServices;
      }

      this.isLoading = true;
      const response = await baanRueJaiClient.admin.subscriptions.services.search(
        params
      );

      this.isLoading = false;
      this.totalServices = response.data.result.count;
      return response.data.result.services;
    },
    handleSelectService(service) {
      this.updateInput([...this.value, service]);
    },
    async handleClickAddAllButton() {
      this.isLoading = true;
      let serviceToAdd;
      if (this.query === '') {
        const response = await baanRueJaiClient.admin.subscriptions.services.list()
        serviceToAdd = response.data.result.map(service => {
          const {
            id,
            name,
            short_description: shortDescription,
            description,
            icon_url: iconUrl,
            options,
            quota
          } = service;
          const provider = {
            ...service.provider,
            logoUrl: service.provider.logo_url
          };
          return {
            id,
            iconUrl,
            name,
            shortDescription,
            description,
            provider,
            options,
            quota
          };
        });
      } else {
        const response = await baanRueJaiClient.admin.subscriptions.services.search({name: this.query, take: 0})
        console.log(response);
        serviceToAdd = response.data.result.services.map(service => {
          const {
            id,
            name,
            short_description: shortDescription,
            description,
            icon_url: iconUrl,
            options,
            quota
          } = service;
          const provider = {
            ...service.provider,
            logoUrl: service.provider.logo_url
          };
          return {
            id,
            iconUrl,
            name,
            shortDescription,
            description,
            provider,
            options,
            quota
          };
        });
      }
      this.updateInput(serviceToAdd);
      this.isLoading = false;
    },
    handleRemoveService(service) {
      this.updateInput(this.value.filter(({ id }) => service.id !== id));
    },
    handleRemoveAllService() {
      if (!confirm("คุณต้องการลบรายการบริการใช่หรือไม่")) {
        return;
      }

      this.updateInput([]);
    },
    updateInput(value) {
      this.$emit("input", value);
    },
    compareService(service, service2) {
      return service.id === service2.id;
    },
    handleQueryChange(value) {
      this.query = value;
    },
    watchQuery: debounce(function() {
      this.resetData();
      this.setupServices();
      this.$refs.searchableServiceList.resetLoading();
    }, 1.5),
    resetData() {
      this.allServices = [];
      this.totalServices = 1;
      this.page = 1;
      this.hasMoreServices = false;
    }
  }
};
</script>

<style lang="scss" scoped>
@import "~@/lib/scss/variables";

.service-selector {
  display: grid;
  grid-template-columns: 1fr 1fr;

  > .search-list {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  > .selected-list {
    border-left-width: 0;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }
}

.service-selector.disabled {
  display: block;

  > .selected-list {
    border-left-width: 1px;
    border-radius: $border-radius;
  }
}
</style>



// WEBPACK FOOTER //
// src/lib/components/ServiceSelector/ServiceSelector.vue